import type { FormatDateOptions } from 'react-intl';

export const VERSION_HISTORY_RETENTION_DAYS: number = 31;
export const MILLISECONDS_IN_DAY: number = 24 * 60 * 60 * 1000;

export const isVersionWithinRetention = (
	updatedTime: string,
	cutoffTime: number = Date.now() - VERSION_HISTORY_RETENTION_DAYS * MILLISECONDS_IN_DAY,
): boolean => {
	const versionTimestamp = new Date(updatedTime).getTime();
	return !Number.isNaN(versionTimestamp) && versionTimestamp >= cutoffTime;
};

export interface FilteredVersionsResult<T> {
	autosavedVersions: readonly T[];
	hasFilteredOldVersions: boolean;
	filteredCount: number;
	totalCount: number;
}

export const filterExpiredVersions = <T extends { readonly updatedTime: string }>(
	rawNodes?: readonly T[] | null,
	cutoffTime: number = Date.now() - VERSION_HISTORY_RETENTION_DAYS * MILLISECONDS_IN_DAY,
): FilteredVersionsResult<T> => {
	const totalCount: number = rawNodes?.length ?? 0;
	if (!rawNodes || rawNodes.length === 0) {
		return {
			autosavedVersions: [],
			hasFilteredOldVersions: false,
			filteredCount: 0,
			totalCount: 0,
		};
	}

	const filtered: readonly T[] = rawNodes.filter((version: T): boolean =>
		isVersionWithinRetention(version.updatedTime, cutoffTime),
	);

	return {
		autosavedVersions: filtered,
		hasFilteredOldVersions: filtered.length < totalCount,
		filteredCount: totalCount - filtered.length,
		totalCount,
	};
};

export const getFormattedDate = (
	dateInputString: string,
	formatDate: (
		value: Parameters<Intl.DateTimeFormat['format']>[0],
		options: FormatDateOptions,
	) => string,
	formatTime: (
		value: Parameters<Intl.DateTimeFormat['format']>[0],
		options: FormatDateOptions,
	) => string,
): string => {
	const dateString = new Date(dateInputString);
	const formattedDate: string = formatDate(dateString, {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	});

	const formattedTime: string = formatTime(dateString, {
		hour: 'numeric',
		minute: 'numeric',
		hour12: true,
	});

	return `${formattedDate} ${formattedTime.replace('AM', 'a.m.').replace('PM', 'p.m.')}`;
};
